import { ChevronDown, ChevronRight } from 'lucide-react';
import { FC } from 'react';

import { ActiveClustersSectionProps } from './types';

/**
 * ActiveClustersSection - Display and select active graph clusters
 *
 * This section displays all currently active clusters in the graph visualization,
 * showing their color indicators, names, and node counts. Users can click clusters
 * to select them for filtering or inspection.
 *
 * Features:
 * - Color-coded cluster indicators
 * - Cluster node counts (visible/total)
 * - Selection highlighting with brand color
 * - Visual warning for clusters with no visible nodes
 * - Collapsible section with accordion behavior
 * - Truncation for long cluster names
 *
 * @param clusters - Array of cluster objects with value, nodes, color, count
 * @param selectedClusterValue - Currently selected cluster value
 * @param onClusterClick - Callback when cluster is clicked
 * @param activeAttribute - Currently active clustering attribute
 * @param isExpanded - Whether section is expanded
 * @param onToggle - Callback to toggle section expansion
 */
export const ActiveClustersSection: FC<ActiveClustersSectionProps> = ({
  clusters,
  selectedClusterValue,
  onClusterClick,
  activeAttribute,
  isExpanded,
  onToggle,
}) => {
  // Only show this section when clustering is active
  if (!activeAttribute) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="border-chariot-stroke mb-2 border-t" />
      <button
        onClick={onToggle}
        className="text-chariot-text-primary hover:text-chariot-text-primary-hover flex items-center gap-1 p-0 text-left text-sm font-semibold transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="size-4 shrink-0" />
        ) : (
          <ChevronRight className="size-4 shrink-0" />
        )}
        View Active Clusters
      </button>
      {isExpanded && (
        <div className="ml-5 flex flex-col gap-2">
          {clusters.length === 0 ? (
            <span className="text-chariot-text-secondary text-sm italic">
              No clusters available
            </span>
          ) : (
            clusters.map(cluster => {
              const isSelected = cluster.value === selectedClusterValue;
              const hasVisibleAssets = cluster.nodes.length > 0;
              return (
                <button
                  key={cluster.value}
                  onClick={() => {
                    onClusterClick?.(cluster.value, cluster.nodes);
                  }}
                  className={`hover:text-chariot-text-primary flex items-center gap-3 text-sm transition-colors ${isSelected ? 'text-chariot-brand-primary font-bold' : hasVisibleAssets ? 'text-chariot-text-secondary' : 'text-chariot-text-secondary/50'}`}
                  title={
                    !hasVisibleAssets
                      ? `${cluster.value} has ${cluster.count} total assets but none are visible in current view`
                      : `${cluster.value}: ${cluster.nodes.length} visible / ${cluster.count} total assets`
                  }
                >
                  <div
                    className="size-4 min-w-4 shrink-0 rounded-full"
                    style={{
                      backgroundColor: isSelected ? '#7A5AF1' : cluster.color,
                      border: `1px solid ${isSelected ? '#7A5AF1' : cluster.color.replace('0.2', '0.6')}`,
                      opacity: hasVisibleAssets ? 1 : 0.5,
                    }}
                  />
                  <span className="truncate text-left select-none">
                    {cluster.value.length > 30
                      ? `${cluster.value.substring(0, 27)}...`
                      : cluster.value}{' '}
                    ({cluster.count ?? cluster.nodes.length})
                    {!hasVisibleAssets && ' ⚠'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
